/**
 * saleInvoiceFormTypes.ts — typ formularza faktury sprzedaży + jego pusty stan.
 *
 * Relokowane z dawnego `SaleInvoiceDataTab.tsx` (usunięty — silnik formatek,
 * unifikacja-formatek Krok 2: `SaleInvoiceDetail` renderuje teraz przez
 * `RecursiveFormRenderer` / `saleInvoiceEngineConfig`, a nie przez ten
 * komponent). Typ i stała nadal są potrzebne — `SaleInvoiceDetail`,
 * `invoiceEngineConfig.ts` i testy budują na nich stan formularza.
 */

export interface SaleInvoiceFormData {
  vendorId: string;
  systemNumber: string | null;
  invoiceNumber: string;
  ksefNumber: string | null;
  documentType: string;
  status: string;
  urgency: string;
  correctedInvoiceId: string | null;
  issueDate: string;
  // received_date usunięte — NULL dla faktur sprzedaży
  saleDate: string | null;
  paymentDueDate: string;
  vendorTaxIdCached: string | null;
  vendorNameCached: string | null;
  currencyCode: string;
  exchangeRate: string | null;
  amountNet: string;
  amountVat: string | null;
  amountGross: string;
  paymentMethod: string | null;
  paidAt: string | null;
  vendorBankAccountId: string | null;
  paymentNotes: string | null;
  description: string | null;
  costCenterDefault: string | null;
  rejectionReason: string | null;
  notes: string | null;
  ownerUserId: string | null;
  /** Kategoria dokumentu (oś `record_categories`, kartoteka_key='documents'). Jedna, opcjonalna. */
  categoryId: string | null;
  // T21 (finding CRITICAL — sekcja „Źródło" bez renderera). Read-only w UI:
  // PATCH_BODY_SCHEMA (apps/api/app/api/sale-invoices/[id]/route.ts) NIE
  // przyjmuje tych pól — `source` ustawia backend przy tworzeniu ('manual') lub
  // imporcie ERP/KSeF ('symfonia'/'ksef'), `externalId`/`externalSource` to
  // identyfikatory idempotency importu.
  source: string;
  externalId: string | null;
  externalSource: string | null;
  // Ewidencja KSeF (0159) — podzbiór kierunkowo neutralny. Data i tryb wystawienia są
  // edytowalne; para znaczników czasu wypełnia się wyłącznie wysyłką faktury do KSeF, więc
  // w UI jest tylko do odczytu (trasa zapisu ich nie przyjmuje).
  ksefIssueDate: string | null;
  ksefIssueMode: string | null;
  ksefSubmittedAt: string | null;
  ksefAvailableAt: string | null;
  documentTypeId?: string | null;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const EMPTY_SALE_INVOICE_FORM: SaleInvoiceFormData = {
  vendorId: '',
  systemNumber: null,
  invoiceNumber: '',
  ksefNumber: null,
  documentType: 'VAT',
  status: 'draft',
  urgency: 'normal',
  correctedInvoiceId: null,
  issueDate: today(),
  // received_date intentionally absent
  saleDate: null,
  paymentDueDate: addDays(today(), 14),
  vendorTaxIdCached: null,
  vendorNameCached: null,
  currencyCode: 'PLN',
  exchangeRate: null,
  amountNet: '',
  amountVat: null,
  amountGross: '',
  paymentMethod: 'transfer',
  paidAt: null,
  vendorBankAccountId: null,
  paymentNotes: null,
  description: null,
  costCenterDefault: null,
  rejectionReason: null,
  notes: null,
  ownerUserId: null,
  categoryId: null,
  source: 'manual',
  externalId: null,
  externalSource: null,
  ksefIssueDate: null,
  ksefIssueMode: null,
  ksefSubmittedAt: null,
  ksefAvailableAt: null,
  documentTypeId: null,
};
